import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import { requestListTransaction } from "../../Context/transaction/actions";
import {
  useTransactionDispatch,
  useTransactionState,
} from "../../Context/transaction/context";
import {
  cancelAPIRequest,
  isLastTokenNull,
  paymentMethodName,
} from "../../Utils";
import { Pagination } from "../../Components/Pagination";
import { DateFilter, filterOptions } from "../../Components/DateFilter";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { useAuthState } from "../../Context";
import Badge from "../../Components/Badge";
import TableSkeleton from "../../Components/TableSkeleton";

const TransactionList = () => {
  const { t, i18n } = useTranslation();

  const [page, setpage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[3]);
  const [selectedMonth, setSelectedMonth] = useState({});
  const [selectedYear, setSelectedYear] = useState({});
  const [dateFilter, setDateFilter] = useState({});

  const dispatch = useTransactionDispatch();
  const { user } = useAuthState();
  const { request, transactions, nextTokens, loading, error } =
    useTransactionState();

  useEffect(() => {
    if (user.id) {
      let createdAt = {};

      if (selectedFilter.value === "TODAY") {
        const startOfDay = dayjs().startOf("day").toISOString();
        const endOfDay = dayjs().endOf("day").toISOString();
        createdAt = {
          between: [startOfDay, endOfDay],
        };
      } else if (selectedFilter.value === "LAST_HOUR") {
        const startOfLastHour = dayjs().subtract(1, "hour").toISOString();
        const endOfNow = dayjs().toISOString();
        createdAt = {
          between: [startOfLastHour, endOfNow],
        };
      } else if (selectedFilter.value === "YESTERDAY") {
        const startOfYesterday = dayjs()
          .subtract(1, "day")
          .startOf("day")
          .toISOString();
        const endOfYesterday = dayjs()
          .subtract(1, "day")
          .endOf("day")
          .toISOString();
        createdAt = {
          between: [startOfYesterday, endOfYesterday],
        };
      } else if (selectedFilter.value === "WEEK") {
        createdAt = {
          gt: dayjs().subtract(7, "day").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "MONTH") {
        createdAt = {
          gt: dayjs().subtract(1, "month").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "OTHER") {
        if (selectedMonth.value && selectedYear.value) {
          const actualMonthValue = parseInt(selectedMonth.value, 10);
          const firstDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .startOf("month")
            .toISOString();
          const lastDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .endOf("month")
            .toISOString();

          createdAt = {
            between: [firstDayOfMonth, lastDayOfMonth],
          };
        }
      }

      setDateFilter(createdAt);

      onLoad(createdAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedFilter, selectedMonth, selectedYear, user.id]);

  const onLoad = (updateFilter) => {
    if (request) {
      cancelAPIRequest(request);
    }
    setpage(1);
    dispatch(
      requestListTransaction(dispatch, {
        page: 1,
        limit: 10,
        createdAt: updateFilter,
        userID: user.id,
      })
    );
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListTransaction(dispatch, {
        page: nextPage,
        limit: 10,
        createdAt: dateFilter,
        nextToken: nextTokens[nextPage],
        userID: user.id,
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListTransaction(dispatch, {
        page: prevPage,
        createdAt: dateFilter,
        nextToken: nextTokens[prevPage],
        userID: user.id,
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListTransaction(dispatch, {
        page: selectedPage,
        createdAt: dateFilter,
        nextToken: nextTokens[selectedPage],
        userID: user.id,
      });
    }
  };

  return (
    <div>
      <div className="flex flex-col flex-wrap sm:flex-row sm:items-center sm:w-full justify-between gap-4 mb-4">
        <div className="flex flex-col flex-wrap sm:flex-row gap-4">
          <DateFilter
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("ID")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Method")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Total")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Description")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Date")}
                    </th>
                  </tr>
                </thead>

                {loading ? (
                  <TableSkeleton rows={3} columns={6} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => {
                        const shortId = transaction.id.substr(
                          transaction.id.length - 5
                        );
                        return (
                          <tr key={transaction.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap uppercase">
                              <a href={`/transaction/${transaction.id}`}>
                                {shortId}
                              </a>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {t(
                                paymentMethodName[transaction.paymentType] ||
                                  transaction.paymentType
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={transaction.totalPay}
                                thousandSeparator={"."}
                                decimalSeparator={","}
                                prefix={"Rp"}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {transaction.status === "PENDING" ? (
                                <Badge title="Pending" type="alert" />
                              ) : null}
                              {transaction.status === "SUCCESS" ? (
                                <Badge title="Success" type="success" />
                              ) : null}
                              {transaction.status === "FAILED" ? (
                                <Badge title="Failed" type="danger" />
                              ) : null}
                              {transaction.status === "EXPIRED" ? (
                                <Badge title="Expired" type="secondary" />
                              ) : null}
                              {transaction.status === "REFUNDED" ? (
                                <Badge title="Refunded" type="secondary" />
                              ) : null}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-pre-wrap">
                              {transaction.description}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {dayjs(transaction.createdAt)
                                .locale(i18n.language)
                                .format("DD MMM YYYY")}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={6}
                        error={error}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "No Transactions"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionList;
